"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Footer from "./Footer";
import {
  BarChart3,
  Bell,
  Bus,
  Calendar,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  GraduationCap,
  MapPin,
  MessageSquare,
  Package,
  Shield,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
  Route,
  Truck,
  AlertTriangle,
  Boxes,
  ShoppingCart,
  ClipboardList,
  Warehouse,
} from "lucide-react";
import ERPLeadBot from "./ERPLeadBot";

const allProducts = {
  school: {
    label: "School Management",
    color: "blue",
    categories: [
      {
        id: "academics",
        name: "Academics",
        features: [
          {
            icon: GraduationCap,
            title: "Grade Management",
            description: "Track student performance, generate report cards, and analyze academic trends.",
            benefits: ["Automated grade calculations", "Custom grading scales", "Progress tracking", "Report card generation"],
          },
          {
            icon: Calendar,
            title: "Automated Timetable",
            description: "Create optimized schedules with automatic conflict detection for teachers and students.",
            benefits: ["Smart scheduling algorithm", "Conflict detection", "Resource optimization", "One-click generation"],
          },
          {
            icon: FileText,
            title: "Assignment Management",
            description: "Create, distribute, and grade assignments digitally with integrated submission tracking.",
            benefits: ["Online submissions", "Plagiarism detection", "Automated grading", "Deadline reminders"],
          },
        ],
      },
      {
        id: "attendance",
        name: "Attendance & Tracking",
        features: [
          {
            icon: UserCheck,
            title: "Attendance System",
            description: "Mark attendance digitally with biometric integration, real-time updates, and automated parent notifications.",
            benefits: ["Biometric integration", "Real-time updates", "Automated notifications", "Attendance reports"],
          },
          {
            icon: Bus,
            title: "Bus Tracking",
            description: "Track school buses in real-time with GPS technology, ensuring student safety.",
            benefits: ["Live GPS tracking", "Route optimization", "Arrival notifications", "Safety alerts"],
          },
          {
            icon: MapPin,
            title: "Geo-Fencing",
            description: "Set up virtual boundaries and receive alerts when students arrive or leave designated areas.",
            benefits: ["Custom zones", "Entry/exit alerts", "Safety monitoring", "Location history"],
          },
        ],
      },
      {
        id: "finance",
        name: "Finance",
        features: [
          {
            icon: DollarSign,
            title: "Fee Management",
            description: "Streamline fee collection with multiple payment methods, automated reminders, and detailed reporting.",
            benefits: ["Multiple payment options", "Automated reminders", "Payment history", "Financial reports"],
          },
          {
            icon: Wallet,
            title: "Payroll Management",
            description: "Process staff salaries automatically with tax calculations, deductions, and salary slip generation.",
            benefits: ["Automated calculations", "Tax compliance", "Salary slips", "Payment history"],
          },
          {
            icon: CreditCard,
            title: "Expense Tracking",
            description: "Monitor and manage school expenses with categorization, approval workflows, and budget tracking.",
            benefits: ["Expense categorization", "Approval workflows", "Budget alerts", "Expense reports"],
          },
        ],
      },
      {
        id: "communication",
        name: "Communication",
        features: [
          {
            icon: MessageSquare,
            title: "Parent Portal",
            description: "Enable parents to stay connected with teachers, view grades, and receive real-time updates.",
            benefits: ["Direct messaging", "Progress reports", "Event calendar", "Mobile access"],
          },
          {
            icon: Bell,
            title: "Notifications",
            description: "Send instant notifications via SMS, email, and push notifications to keep everyone informed.",
            benefits: ["Multi-channel delivery", "Custom templates", "Scheduled messages", "Delivery tracking"],
          },
          {
            icon: Users,
            title: "Staff Collaboration",
            description: "Facilitate communication between teachers, administrators, and staff.",
            benefits: ["Group discussions", "File sharing", "Task assignment", "Meeting scheduler"],
          },
        ],
      },
      {
        id: "analytics",
        name: "Analytics",
        features: [
          {
            icon: BarChart3,
            title: "Analytics Dashboard",
            description: "Gain insights with comprehensive analytics covering academics, attendance, finances, and more.",
            benefits: ["Real-time data", "Custom reports", "Visual analytics", "Export options"],
          },
          {
            icon: TrendingUp,
            title: "Performance Tracking",
            description: "Monitor student and teacher performance with detailed metrics and trend analysis.",
            benefits: ["Performance metrics", "Trend analysis", "Comparison tools", "Improvement tracking"],
          },
          {
            icon: Clock,
            title: "Audit Logs",
            description: "Track all system activities with detailed logs for compliance and security.",
            benefits: ["Complete audit trail", "User activity logs", "Security monitoring", "Compliance reports"],
          },
        ],
      },
      {
        id: "security",
        name: "Security",
        features: [
          {
            icon: Shield,
            title: "Data Security",
            description: "Enterprise-grade security with encryption, regular backups, and compliance with data protection regulations.",
            benefits: ["256-bit encryption", "Daily backups", "GDPR compliant", "Role-based access"],
          },
        ],
      },
    ],
  },
  bus: {
    label: "Bus Management",
    color: "green",
    categories: [
      {
        id: "tracking",
        name: "Live Tracking",
        features: [
          {
            icon: MapPin,
            title: "Real-Time GPS Tracking",
            description: "Monitor every vehicle live on map with 10-second location updates.",
            benefits: ["Live location on map", "10-second refresh", "Historical route replay", "Multi-vehicle view"],
          },
          {
            icon: Route,
            title: "Route Management",
            description: "Plan, assign, and optimize bus routes and stops with a drag-and-drop editor.",
            benefits: ["Route planning", "Stop management", "Distance calculation", "Route optimization"],
          },
          {
            icon: MapPin,
            title: "Geo-Fencing",
            description: "Create virtual zones around stops and schools, get alerts on entry and exit.",
            benefits: ["Custom geo-zones", "Entry/exit alerts", "Safe zone monitoring", "Location history"],
          },
        ],
      },
      {
        id: "fleet",
        name: "Fleet Management",
        features: [
          {
            icon: Truck,
            title: "Vehicle Records",
            description: "Maintain complete records of every vehicle including documents, maintenance history, and insurance.",
            benefits: ["Vehicle profiles", "Document storage", "Maintenance logs", "Insurance tracking"],
          },
          {
            icon: Users,
            title: "Driver Management",
            description: "Manage driver profiles, licenses, duty schedules, and performance records.",
            benefits: ["Driver profiles", "License tracking", "Duty scheduling", "Performance reports"],
          },
          {
            icon: AlertTriangle,
            title: "Maintenance Alerts",
            description: "Get automated reminders for service due dates, insurance renewals, and fitness certificates.",
            benefits: ["Service reminders", "Insurance alerts", "Fitness certificate tracking", "Custom alert schedule"],
          },
        ],
      },
      {
        id: "notifications",
        name: "Parent Alerts",
        features: [
          {
            icon: Bell,
            title: "Automated Notifications",
            description: "Notify parents when the bus leaves, arrives at their stop, and reaches school.",
            benefits: ["Departure alerts", "Stop arrival SMS", "Delay notifications", "Custom message templates"],
          },
          {
            icon: MessageSquare,
            title: "Parent Mobile App",
            description: "Parents can track the bus live, view ETA, and receive notifications from their phone.",
            benefits: ["Live bus map", "ETA display", "Push notifications", "iOS & Android"],
          },
        ],
      },
      {
        id: "bus-analytics",
        name: "Analytics",
        features: [
          {
            icon: BarChart3,
            title: "Fleet Reports",
            description: "Detailed reports on fuel usage, trip history, driver performance, and maintenance costs.",
            benefits: ["Fuel reports", "Trip logs", "Driver scorecards", "Cost analysis"],
          },
          {
            icon: Clock,
            title: "Trip History",
            description: "Access complete trip-by-trip history with timestamps, routes, and incidents.",
            benefits: ["Full trip logs", "Timestamp records", "Route history", "Incident reports"],
          },
        ],
      },
    ],
  },
  inventory: {
    label: "Inventory Management",
    color: "purple",
    categories: [
      {
        id: "stock",
        name: "Stock Control",
        features: [
          {
            icon: Boxes,
            title: "Real-Time Stock Tracking",
            description: "Monitor stock levels live across all locations, no more manual counting.",
            benefits: ["Live stock levels", "Multi-location support", "Barcode scanning", "Stock history"],
          },
          {
            icon: AlertTriangle,
            title: "Low-Stock Alerts",
            description: "Get automatic alerts when any item falls below your defined minimum quantity.",
            benefits: ["Custom thresholds", "Email & SMS alerts", "Dashboard warnings", "Auto-reorder trigger"],
          },
          {
            icon: Warehouse,
            title: "Multi-Location Inventory",
            description: "Manage stock across multiple warehouses or branches from a single dashboard.",
            benefits: ["Multiple locations", "Transfer management", "Location-wise reports", "Central oversight"],
          },
        ],
      },
      {
        id: "procurement",
        name: "Procurement",
        features: [
          {
            icon: ShoppingCart,
            title: "Purchase Orders",
            description: "Create, track, and manage purchase orders from request to delivery.",
            benefits: ["PO creation", "Approval workflow", "Delivery tracking", "PO history"],
          },
          {
            icon: Users,
            title: "Supplier Management",
            description: "Maintain supplier profiles, payment terms, and order history in one place.",
            benefits: ["Supplier profiles", "Payment terms", "Order history", "Supplier ratings"],
          },
        ],
      },
      {
        id: "inventory-analytics",
        name: "Analytics",
        features: [
          {
            icon: BarChart3,
            title: "Inventory Reports",
            description: "Get insights on stock movement, turnover rate, wastage, and valuation.",
            benefits: ["Stock movement reports", "Turnover analysis", "Wastage tracking", "Valuation reports"],
          },
          {
            icon: ClipboardList,
            title: "Audit & Reconciliation",
            description: "Run physical stock audits and reconcile against system records with ease.",
            benefits: ["Physical audit tools", "Variance reports", "Reconciliation history", "Audit trails"],
          },
          {
            icon: TrendingUp,
            title: "Demand Forecasting",
            description: "Predict future stock needs based on historical usage patterns.",
            benefits: ["Usage trend analysis", "Seasonal forecasting", "Reorder suggestions", "Export reports"],
          },
        ],
      },
      {
        id: "inv-security",
        name: "Security",
        features: [
          {
            icon: Shield,
            title: "Role-Based Access",
            description: "Control who can view, edit, or approve stock transactions with granular permissions.",
            benefits: ["Custom roles", "Approval workflows", "Audit logs", "Activity tracking"],
          },
        ],
      },
    ],
  },
};

const colorConfig = {
  blue: {
    activeTrigger: "data-[state=active]:bg-blue-600 data-[state=active]:text-white",
    icon: "bg-blue-100 text-blue-600",
    pill: "bg-blue-600 text-white",
    pillInactive: "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50",
    cardHover: "hover:border-blue-200",
  },
  green: {
    activeTrigger: "data-[state=active]:bg-green-600 data-[state=active]:text-white",
    icon: "bg-green-100 text-green-600",
    pill: "bg-green-600 text-white",
    pillInactive: "bg-white text-green-600 border border-green-200 hover:bg-green-50",
    cardHover: "hover:border-green-200",
  },
  purple: {
    activeTrigger: "data-[state=active]:bg-purple-600 data-[state=active]:text-white",
    icon: "bg-purple-100 text-purple-600",
    pill: "bg-purple-600 text-white",
    pillInactive: "bg-white text-purple-600 border border-purple-200 hover:bg-purple-50",
    cardHover: "hover:border-purple-200",
  },
};

export default function Features() {
  const [activeProduct, setActiveProduct] = useState("school");

  const product = allProducts[activeProduct];
  const c = colorConfig[product.color];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
              Features Built for Every Product
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Explore the full feature set for each of our products, School, Bus, and Inventory management.
            </p>

            {/* Product Selector */}
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(allProducts).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => setActiveProduct(key)}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeProduct === key
                      ? colorConfig[val.color].pill
                      : colorConfig[val.color].pillInactive
                  }`}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature Tabs */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue={product.categories[0].id} key={activeProduct} className="w-full">
            <TabsList className="mb-12 grid h-auto w-full gap-2 bg-transparent p-0"
              style={{ gridTemplateColumns: `repeat(${product.categories.length}, minmax(0, 1fr))` }}
            >
              {product.categories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className={`whitespace-normal border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 sm:text-sm ${c.activeTrigger}`}
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {product.categories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="mt-0">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {category.features.map((feature, index) => (
                    <Card key={index} className={`border-2 transition-colors ${c.cardHover}`}>
                      <CardHeader>
                        <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-lg ${c.icon}`}>
                          <feature.icon className="h-7 w-7" />
                        </div>
                        <CardTitle className="text-xl">{feature.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="mb-4 text-gray-600 text-sm">{feature.description}</p>
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-gray-900">Key Benefits:</div>
                          <ul className="space-y-1">
                            {feature.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                <span className={`h-1.5 w-1.5 rounded-full ${
                                  product.color === "blue" ? "bg-blue-600" :
                                  product.color === "green" ? "bg-green-600" : "bg-purple-600"
                                }`} />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
